import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import formEditorActions from '../../../stores/FormEditor/actions';
import HeroImageUpload from '../../../common/HeroImageUpload';

import './formDetails.css';
import DescriptionEditor from './DescriptionEditor';
import AssigneeSearch from './AssigneeSearch';
import DeadlineSelector from './DeadlineSelector';
import ColorPicker from './ColorPicker';

class FormDetails extends PureComponent {
  handleChange = (ev) => {
    const {
      onDetailsChange
    } = this.props;

    const {
      target
    } = ev;

    onDetailsChange(target);
  };

  handleChangePrivacy = () => {};

  handleChangeTaskSummary = (ev) => {
    const {
      onTaskSummaryChange
    } = this.props;

    const {
      value
    } = ev.target;

    onTaskSummaryChange(value);
  };

  render() {
    const {
      assignTasksTo,
      tasksDeadline,
      title,
      description,
      confirmationText,
      onDeadlineChange,
      onSearchUsers,
      onTaskAssigneesChange,
      taskSummary,
      headerImage,
      buttonLabel,
      buttonColor,
      onButtonLabelChange,
      onButtonColorChange,
      onConfirmationTextChange,
      onHeaderImageChange
    } = this.props;

    return (
      <div className="form-properties-container">
        <h2>Form properties</h2>
        <div className="form-group">
          <label htmlFor="title">
            Title
          </label>
          <input
            className="form-control"
            type="text"
            id="title"
            name="title"
            value={title}
            onChange={this.handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="description">
            Description
          </label>
          <DescriptionEditor
            markdown={description}
            onMarkdownChange={md => this.handleChange({ target: { name: 'description', value: md } })}
          />
          <p className="help-block">
            Select a text to apply bold, italic, links, lists, etc.
          </p>
        </div>
        {/* Privacy moved to topbar */}
        <div className="form-group">
          <label htmlFor="hero-image-upload">
            Hero image
          </label>
          <HeroImageUpload
            headerImage={headerImage || ''}
            onHeaderImageChange={onHeaderImageChange}
          />
          <p className="help-block">
            Upload a banner image. For best results, use a 1340×430px image.
          </p>
        </div>
        <div className="form-group">
          <label>
            Button label
          </label>
          <input className="form-control" type="text" id="button-label" name="buttonLabel" value={buttonLabel} onChange={({ target: { value } }) => onButtonLabelChange(value)} placeholder="Submit" />
        </div>
        <div className="form-group">
          <label>
            Button color
          </label>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <ColorPicker color={buttonColor} onChange={onButtonColorChange} />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="confirmationText">
            Success message
          </label>
          <DescriptionEditor
            markdown={confirmationText || ''}
            allowHeading
            onMarkdownChange={md => onConfirmationTextChange(md)}
          />
          <p className="help-block">
            Select a text to apply heading, bold, links, lists, etc.
          </p>
        </div>
        <h2>Customize the task</h2>
        <div className="form-group">
          <label>
            Summary
          </label>
          <input
            className="form-control"
            type="text"
            id="task-summary"
            name="task-summary"
            value={taskSummary}
            onChange={this.handleChangeTaskSummary}
          />
          <p className="help-block">
            Using the question ID you can make the task dynamic. Example: &quot;New task from
            #XYZ#&quot; Where, XYZ is the ID of the question.
          </p>
        </div>
        <div className="form-group">
          <label>
            Assign to
          </label>
          <AssigneeSearch
            assignees={assignTasksTo}
            onChange={onTaskAssigneesChange}
            onSearchUsers={onSearchUsers}
          />
          <p className="help-block">
            Assign the task to a participant of the space.
          </p>
        </div>
        <div className="form-group">
          <label>
            Deadline
          </label>
          <DeadlineSelector
            deadline={tasksDeadline}
            onChange={onDeadlineChange}
          />
          <p className="help-block">
            Set a due date for the task.
          </p>
        </div>
      </div>
    );
  }
}

FormDetails.defaultProps = {
  description: null,
  headerImage: null,
  tasksDeadline: -1,
  confirmationText: '',
  buttonLabel: '',
  buttonColor: ''
};

FormDetails.propTypes = {
  // Required props
  assignTasksTo: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  title: PropTypes.string.isRequired,
  // privacy/teamName used in topbar, not here
  taskSummary: PropTypes.string.isRequired,
  confirmationText: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonColor: PropTypes.string,
  
  // Required methods
  onDeadlineChange: PropTypes.func.isRequired,
  onDetailsChange: PropTypes.func.isRequired,
  // onPrivacyChange handled in topbar
  onTaskAssigneesChange: PropTypes.func.isRequired,
  onSearchUsers: PropTypes.func.isRequired,
  onTaskSummaryChange: PropTypes.func.isRequired,
  onHeaderImageChange: PropTypes.func.isRequired,
  onButtonLabelChange: PropTypes.func.isRequired,
  onButtonColorChange: PropTypes.func.isRequired,
  onConfirmationTextChange: PropTypes.func.isRequired,

  // Optional props
  description: PropTypes.string,
  tasksDeadline: PropTypes.number,
  headerImage: PropTypes.string
};

const mapStateToProps = ({ formEditor }) => {
  const {
    form
  } = formEditor;

  const formSchema = form.JSONSchema || {};

  return {
    assignTasksTo: form.assignTasksTo,
    description: formSchema.description,
    confirmationText: form.confirmationText,
    privacy: _get(formEditor, 'form.privacy'),
    tasksDeadline: form.tasksDeadline,
    title: formSchema.title,
    taskSummary: form.taskSummary,
    headerImage: form.headerImage,
    buttonLabel: form.buttonLabel,
    buttonColor: form.buttonColor
  };
};

const mapDispatchToProps = dispatch => ({
  onDeadlineChange: deadline => dispatch(
    formEditorActions.formTaskDeadlineChange(deadline)
  ),
  onTaskSummaryChange: taskSummary => dispatch(
    formEditorActions.formTaskSummaryChange(taskSummary)
  ),
  onDetailsChange: target => dispatch(
    formEditorActions.formDetailsChange(target)
  ),
  onPrivacyChange: (privacy, token) => dispatch(
    formEditorActions.formPrivacyChange(privacy, token)
  ),
  onSearchUsers: (searchText, callback) => {
    dispatch(
      formEditorActions.searchUsers(searchText, callback)
    );
  },
  onTaskAssigneesChange: assignees => dispatch(
    formEditorActions.formTaskAssigneesChange(assignees)
  ),
  onHeaderImageChange: headerImage => dispatch(
    formEditorActions.formHeaderImageChange(headerImage)
  ),
  onButtonLabelChange: buttonLabel => dispatch(
    formEditorActions.formButtonLabelChange(buttonLabel)
  ),
  onButtonColorChange: buttonColor => dispatch(
    formEditorActions.formButtonColorChange(buttonColor)
  ),
  onConfirmationTextChange: confirmationText => dispatch(
    formEditorActions.formConfirmationTextChange(confirmationText)
  )
});

const FormDetailsRedux = connect(
  mapStateToProps,
  mapDispatchToProps
)(FormDetails);

export { FormDetails };
export default FormDetailsRedux;
